import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import Popper from "@mui/material/Popper";
import { useTheme, styled } from "@mui/material/styles";
import { VariableSizeList, ListChildComponentProps } from "react-window";
import Typography from "@mui/material/Typography";

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty("group")) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {dataSet[1]}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
// @ts-ignore
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: React.ReactElement[] = [];
  (children as React.ReactElement[]).forEach(
    (item: React.ReactElement & { children?: React.ReactElement[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const itemCount = itemData?.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: React.ReactElement) => {
    if (child.hasOwnProperty("group")) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

function random(length: number) {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";

  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }

  return result;
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

export function VirtualizeCode(props: any) {
  return (
    <Autocomplete
      id="virtualize-demo"
      size="small"
      disableListWrap
      PopperComponent={StyledPopper}
      value={props.value}
      inputValue={props.inputValue}
      onInputChange={(event, newInputValue) => {
        const key = `index-${props.index}`;
        props.setInputValue({
          ...props.originalInputValue,
          [key]: newInputValue,
        });
      }}
      ListboxComponent={ListboxComponent}
      options={props.options}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Code"
          required
          disabled={props.disabled}
        />
      )}
      renderOption={(props, option, state) =>
        [props, option, state.index] as React.ReactNode
      }
      onChange={(event: any, newValue: any) => {
        const key = `index-${props.index}`;
        props.setValue({
          ...props.originalValue,
          [key]: newValue,
        });
        const event1 = {
          target: {
            value: newValue,
          },
        };
        props.handleChange(
          event1,
          props.setFieldValue,
          `steelpertrays[${props.index}].code`,
          props.index
        );
      }}
      renderGroup={(params) => params as any}
    />
  );
}

export function VirtualizeName(props: any) {
  return (
    <Autocomplete
      id="virtualize-demo1"
      size="small"
      disableListWrap
      PopperComponent={StyledPopper}
      value={props.value}
      inputValue={props.inputValue}
      onInputChange={(event, newInputValue) => {
        props.setInputValue(newInputValue);
      }}
      ListboxComponent={ListboxComponent}
      options={props.options}
      renderInput={(params) => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            sx: {
              width: "226px",
              height: "35px",
              fontSize: "14px",
            },
          }}
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              fontSize: "14px",
            },
          }}
          label="Name"
        />
      )}
      renderOption={(props, option, state) =>
        [props, option, state.index] as React.ReactNode
      }
      onChange={(event: any, newValue: any) => {
        props.setValue(newValue);
        props.handleChange(newValue, `names`);
      }}
      renderGroup={(params) => params as any}
    />
  );
}
